// src/pages/UserManagement_Enhanced.jsx

import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Avatar,
  Modal,
  Form,
  message,
  Popconfirm,
  Statistic,
  Row,
 Col,
  Alert,
  Typography,
  Divider,
  Tooltip,
} from "antd";

import {
  UserAddOutlined,
  SearchOutlined,
  DeleteOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";

import { useAuth } from "../context/AuthContext";
import userService from "../services/userService";
import apiClient from "../utils/apiClient";
import PasswordResetModal from "../components/PasswordResetModal";

import "./UserManagement.css";

const { Title, Text } = Typography;
const { Option } = Select;

const UserManagement_Enhanced = () => {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [addUserModalVisible, setAddUserModalVisible] =
    useState(false);

  const [passwordResetModalVisible, setPasswordResetModalVisible] =
    useState(false);

  const [selectedUser, setSelectedUser] = useState(null);

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });

  const [form] = Form.useForm();

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [currentUser]);

  /* =====================================================
     FETCH USERS
  ===================================================== */

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response =
        await userService.getUsers();

const userData =
  response?.data?.users ||
  response?.users ||
  [];

setUsers(
  Array.isArray(userData)
    ? userData
    : []
);

    } catch (error) {
      console.error(
        "Error fetching users:",
        error
      );

      message.error(
        "Failed to fetch users"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     FETCH STATS
  ===================================================== */

  const fetchStats = async () => {
    try {
      const response =
        await userService.getUserStats();

setStats({
  totalUsers:
    response?.data?.overview
      ?.totalUsers || 0,

  activeUsers:
    response?.data?.overview
      ?.activeUsers || 0,

  inactiveUsers:
    response?.data?.overview
      ?.inactiveUsers || 0,
});


    } catch (error) {
      console.error(
        "Error fetching stats:",
        error
      );
    }
  };

  /* =====================================================
     CREATE USER
  ===================================================== */

const handleAddUser = async (
  values
) => {
  try {
    setLoading(true);

    const result =
      await userService.createUser(
        values
      );

    if (result?.success) {
      message.success(
        result?.message ||
          "User created successfully"
      );

      form.resetFields();

      setAddUserModalVisible(
        false
      );

      await fetchUsers();
      await fetchStats();

      // OPTIONAL:
      // show generated setup link

      if (
        result?.data
          ?.verificationLink
      ) {
        setSelectedUser({
          name: values.name,
          email: values.email,
          resetLink:
            result.data
              .verificationLink,
        });

        setPasswordResetModalVisible(
          true
        );
      }

    } else {
      message.error(
        result?.message ||
          "Failed to create user"
      );
    }

  } catch (error) {
    console.error(
      "Error creating user:",
      error
    );

    message.error(
      error?.response?.data
        ?.message ||
        error?.message ||
        "Failed to create user"
    );

  } finally {
    setLoading(false);
  }
};

  /* =====================================================
     DELETE USER
  ===================================================== */

  const handleDeleteUser = async (
    userId
  ) => {
    try {
      await userService.deleteUser(
        userId
      );

      message.success(
        "User deleted successfully"
      );

      fetchUsers();
      fetchStats();

    } catch (error) {
      console.error(
        "Error deleting user:",
        error
      );

      message.error(
        "Failed to delete user"
      );
    }
  };

  /* =====================================================
     PASSWORD RESET
  ===================================================== */

  const handlePasswordReset =
    async (user) => {
      try {
        if (
          user._id === currentUser._id
        ) {
          const response =
            await apiClient.post(
              "/auth/forgot-password",
              {
                email: user.email,
              }
            );

          if (
            response.data.success
          ) {
            message.success(
              "Password reset link generated"
            );

            setSelectedUser({
              ...user,
              resetLink:
                response.data
                  .resetLink,
            });

            setPasswordResetModalVisible(
              true
            );
          }
        } else {
          const response =
            await apiClient.post(
              `/users/${user._id}/reset-password`
            );

          if (
            response.data.success
          ) {
            message.success(
              "Setup link generated"
            );

            setSelectedUser({
              ...user,
              resetLink:
                response.data
                  .resetLink,
            });

            setPasswordResetModalVisible(
              true
            );
          }
        }
      } catch (error) {
        console.error(
          "Reset link error:",
          error
        );

        message.error(
          "Failed to generate setup link"
        );
      }
    };

  /* =====================================================
     ROLE OPTIONS
  ===================================================== */

  const getRoleOptions = () => {
    if (
      currentUser?.role ===
      "superadmin"
    ) {
      return [
        {
          value: "admin",
          label: "Admin",
        },
        {
          value: "sales_manager",
          label: "Sales Manager",
        },
        {
          value: "team_lead",
          label: "Team Lead",
        },
        {
          value: "advisor",
          label: "Advisor",
        },
        {
          value: "reservations",
          label: "Reservations",
        },
        {
          value: "transport",
          label: "Transport",
        },
        {
          value: "accounts",
          label: "Accounts",
        },
      ];
    }

    if (
      currentUser?.role ===
      "admin"
    ) {
      return [
        {
          value: "sales_manager",
          label: "Sales Manager",
        },
        {
          value: "team_lead",
          label: "Team Lead",
        },
        {
          value: "advisor",
          label: "Advisor",
        },
        {
          value: "reservations",
          label: "Reservations",
        },
        {
          value: "transport",
          label: "Transport",
        },
        {
          value: "accounts",
          label: "Accounts",
        },
      ];
    }

    return [];
  };

  /* =====================================================
     FILTER USERS
  ===================================================== */

  const filteredUsers =
    users.filter((user) => {
      const matchesSearch =
        !searchText ||
        user.name
          ?.toLowerCase()
          .includes(
            searchText.toLowerCase()
          ) ||
        user.email
          ?.toLowerCase()
          .includes(
            searchText.toLowerCase()
          );

      const matchesRole =
        !roleFilter ||
        user.role === roleFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter ===
          "active" &&
          user.isActive) ||
        (statusFilter ===
          "inactive" &&
          !user.isActive);

      return (
        matchesSearch &&
        matchesRole &&
        matchesStatus
      );
    });

  /* =====================================================
     TABLE COLUMNS
  ===================================================== */

  const columns = [
    {
      title: "User",

      key: "user",

      render: (record) => (
        <Space>
          <Avatar
            icon={<UserOutlined />}
          />

          <div>
            <div
              style={{
                fontWeight: 500,
              }}
            >
              {record.name}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#666",
              }}
            >
              {record.email}
            </div>
          </div>
        </Space>
      ),
    },

    {
      title: "Role",

      dataIndex: "role",

      key: "role",

      render: (role) => (
        <Tag color="blue">
          {role
            ?.replace("_", " ")
            .toUpperCase()}
        </Tag>
      ),
    },

    {
      title: "Status",

      key: "status",

      render: (record) => (
        <Tag
          color={
            record.isActive
              ? "green"
              : "orange"
          }
        >
          {record.isActive
            ? "Active"
            : "Pending Setup"}
        </Tag>
      ),
    },

    {
      title: "Contact",

      key: "contact",

      render: (record) => (
        <Space>
          {record.phone && (
            <Tooltip
              title={
                record.phone
              }
            >
              <PhoneOutlined />
            </Tooltip>
          )}
        </Space>
      ),
    },

    {
      title: "Actions",

      key: "actions",

      render: (record) => (
        <Space>
          {!record.isActive ? (
            <Tooltip title="Generate Setup Link">
              <Button
                type="text"
                icon={
                  <KeyOutlined />
                }
                onClick={() =>
                  handlePasswordReset(
                    record
                  )
                }
              />
            </Tooltip>
          ) : (
            <CheckCircleOutlined
              style={{
                color: "green",
              }}
            />
          )}

          {(currentUser?.role ===
            "admin" ||
            currentUser?.role ===
              "superadmin") && (
            <Popconfirm
              title="Delete user?"
              onConfirm={() =>
                handleDeleteUser(
                  record._id
                )
              }
            >
              <Button
                type="text"
                icon={
                  <DeleteOutlined />
                }
                danger
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="user-management-enhanced">

      {/* =====================================================
         HEADER
      ===================================================== */}

      <div className="page-header">
        <Title level={2}>
          <TeamOutlined
            style={{
              marginRight: 8,
              color: "#1890ff",
            }}
          />

          User Management
        </Title>

        <Text type="secondary">
          Manage users, access,
          onboarding and roles
        </Text>
      </div>

      {/* =====================================================
         STATS
      ===================================================== */}

      <Row
        gutter={16}
        style={{
          marginBottom: 24,
        }}
      >

<Col
  xs={24}
  sm={12}
  md={6}
>
  <Card
    hoverable
    onClick={() =>
      setStatusFilter("")
    }
    style={{
      cursor: "pointer",
      border:
        !statusFilter
          ? "2px solid #1890ff"
          : "1px solid #f0f0f0",
    }}
  >
    <Statistic
      title="Total Users"
      value={
        stats.totalUsers
      }
      prefix={
        <UserOutlined />
      }
    />
  </Card>
</Col>

<Col
  xs={24}
  sm={12}
  md={6}
>
  <Card
    hoverable
    onClick={() =>
      setStatusFilter("active")
    }
    style={{
      cursor: "pointer",
      border:
        statusFilter ===
        "active"
          ? "2px solid #52c41a"
          : "1px solid #f0f0f0",
    }}
  >
    <Statistic
      title="Active Users"
      value={
        stats.activeUsers
      }
      valueStyle={{
        color: "#52c41a",
      }}
      prefix={
        <CheckCircleOutlined />
      }
    />
  </Card>
</Col>

<Col
  xs={24}
  sm={12}
  md={6}
>
  <Card
    hoverable
    onClick={() =>
      setStatusFilter("inactive")
    }
    style={{
      cursor: "pointer",
      border:
        statusFilter ===
        "inactive"
          ? "2px solid #fa8c16"
          : "1px solid #f0f0f0",
    }}
  >
    <Statistic
      title="Pending Invites"
      value={
        stats.inactiveUsers
      }
      valueStyle={{
        color: "#fa8c16",
      }}
      prefix={
        <MailOutlined />
      }
    />
  </Card>
</Col>

<Col
  xs={24}
  sm={12}
  md={6}
>
  <Card
    hoverable
    onClick={() =>
      setStatusFilter("inactive")
    }
    style={{
      cursor: "pointer",
      border:
        statusFilter ===
        "inactive"
          ? "2px solid #f5222d"
          : "1px solid #f0f0f0",
    }}
  >
    <Statistic
      title="Inactive Users"
      value={
        stats.inactiveUsers
      }
      valueStyle={{
        color: "#f5222d",
      }}
      prefix={
        <ClockCircleOutlined />
      }
    />
  </Card>
</Col>

      </Row>

      {/* =====================================================
         FILTERS
      ===================================================== */}

      <Card
        style={{
          marginBottom: 24,
        }}
      >
        <Row
          gutter={16}
          align="middle"
        >
          <Col
            xs={24}
            sm={12}
            md={6}
          >
            <Input
              placeholder="Search users..."
              prefix={
                <SearchOutlined />
              }
              value={searchText}
              onChange={(e) =>
                setSearchText(
                  e.target.value
                )
              }
              allowClear
            />
          </Col>

          <Col
            xs={24}
            sm={12}
            md={6}
          >
            <Select
              placeholder="Filter by role"
              value={roleFilter}
              onChange={
                setRoleFilter
              }
              allowClear
              style={{
                width: "100%",
              }}
            >
              {getRoleOptions().map(
                (option) => (
                  <Option
                    key={
                      option.value
                    }
                    value={
                      option.value
                    }
                  >
                    {option.label}
                  </Option>
                )
              )}
            </Select>
          </Col>

          <Col
            xs={24}
            sm={12}
            md={6}
          >
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={
                setStatusFilter
              }
              allowClear
              style={{
                width: "100%",
              }}
            >
              <Option value="active">
                Active
              </Option>

              <Option value="inactive">
                Inactive
              </Option>
            </Select>
          </Col>

          <Col
            xs={24}
            sm={12}
            md={6}
          >
            <Button
              type="primary"
              icon={
                <UserAddOutlined />
              }
              onClick={() =>
                setAddUserModalVisible(
                  true
                )
              }
              block
            >
              Add User
            </Button>
          </Col>
        </Row>
      </Card>

      {/* =====================================================
         TABLE
      ===================================================== */}

      <Card>
        <Table
          columns={columns}
          dataSource={
            filteredUsers
          }
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* =====================================================
         ADD USER MODAL
      ===================================================== */}

      <Modal
        title="Add New User"
        open={
          addUserModalVisible
        }
        onCancel={() => {
          setAddUserModalVisible(
            false
          );

          form.resetFields();
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Alert
          message="User Setup Link"
          description="A secure setup link will be generated automatically."
          type="info"
          showIcon
          style={{
            marginBottom: 24,
          }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={
            handleAddUser
          }
          size="large"
        >
          <Row gutter={16}>
            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="name"
                label="Full Name"
                rules={[
                  {
                    required: true,
                    message:
                      "Please enter full name",
                  },
                ]}
              >
                <Input placeholder="John Doe" />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  {
                    required: true,
                    message:
                      "Please enter email",
                  },

                  {
                    type: "email",
                    message:
                      "Invalid email",
                  },
                ]}
              >
                <Input placeholder="john@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="role"
                label="Role"
                rules={[
                  {
                    required: true,
                    message:
                      "Please select role",
                  },
                ]}
              >
                <Select
                  placeholder="Select role"
                  showSearch
                >
                  {getRoleOptions().map(
                    (option) => (
                      <Option
                        key={
                          option.value
                        }
                        value={
                          option.value
                        }
                      >
                        {
                          option.label
                        }
                      </Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="department"
                label="Department"
              >
                <Select placeholder="Select department">
                  <Option value="Sales">
                    Sales
                  </Option>

                  <Option value="Operations">
                    Operations
                  </Option>

                  <Option value="Accounts">
                    Accounts
                  </Option>

                  <Option value="Reservations">
                    Reservations
                  </Option>

                  <Option value="Transport">
                    Transport
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="phone"
                label="Phone Number"
              >
                <Input placeholder="+91 9876543210" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Additional Notes"
          >
            <Input.TextArea
              rows={3}
              placeholder="Optional notes..."
            />
          </Form.Item>

          <Divider />

          <Form.Item
            style={{
              marginBottom: 0,
              textAlign: "right",
            }}
          >
            <Space>
              <Button
                onClick={() => {
                  setAddUserModalVisible(
                    false
                  );

                  form.resetFields();
                }}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={
                  <UserAddOutlined />
                }
              >
                Create User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* =====================================================
         PASSWORD RESET MODAL
      ===================================================== */}

      <PasswordResetModal
        visible={
          passwordResetModalVisible
        }
        onClose={() => {
          setPasswordResetModalVisible(
            false
          );

          setSelectedUser(null);
        }}
        userData={selectedUser}
        resetLink={
          selectedUser?.resetLink
        }
      />
    </div>
  );
};

export default UserManagement_Enhanced;
